import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      setMustChangePassword(res.data.must_change_password || false);
    } catch (err) {
      console.error("Auth error:", err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, must_change_password } = res.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setMustChangePassword(must_change_password || false);
    return { user: userData, mustChangePassword: must_change_password };
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setMustChangePassword(false);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    setMustChangePassword(false);
  };

  // Permission check helper
  useEffect(() => {
    if (!token) return;
    let active = true;
    const refreshAccess = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setUser(data);
      } catch { /* A temporary network failure must not discard the current session. */ }
    };
    const timer = setInterval(refreshAccess, 60000);
    window.addEventListener("focus", refreshAccess);
    document.addEventListener("visibilitychange", refreshAccess);
    return () => { active = false; clearInterval(timer); window.removeEventListener("focus", refreshAccess); document.removeEventListener("visibilitychange", refreshAccess); };
  }, [token]);

  // Permission check helper
  const hasPermission = useCallback((permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions[permission] === true;
  }, [user]);

  // Role check helper
  const hasRole = useCallback((roles) => {
    if (!user || !user.role) return false;
    if (typeof roles === "string") return user.role === roles;
    return roles.includes(user.role);
  }, [user]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.role === "admin";
  }, [user]);

  // Tab access check (admin = all tabs; allowed_tabs null = all tabs)
  const canAccessTab = useCallback((tab) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!user.allowed_tabs) return true;
    return user.allowed_tabs.includes(tab);
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      changePassword,
      mustChangePassword,
      hasPermission,
      hasRole,
      isAdmin,
      canAccessTab
    }}>
      {children}
    </AuthContext.Provider>
  );
};
